<script setup>
  import { Combobox, ComboboxButton, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
  import { watchThrottled } from '@vueuse/core'

  const props = defineProps({
    modelValue: { type: [String, Number], default: null },
    placeholder: { type: String, default: 'Rechercher un employeur' },
    required: { type: Boolean, default: false },
    error: { type: String, default: '' }
  })

  const emit = defineEmits(['update:modelValue'])

  const { find } = useStrapi4()
  const query = ref('')
  const employerSuggestions = ref([])
  const isLoading = ref(false)

  // Store selected employer data to keep name and ID
  const selectedEmployerData = ref(null)

  // Selected employer computed property for two-way binding
  const selectedEmployer = computed({
    get() {
      return props.modelValue
    },
    set(value) {
      // Store employer data when selected
      if (value) {
        const employer = employerSuggestions.value.find(emp => emp.id === value)
        if (employer) {
          selectedEmployerData.value = employer
        }
      } else {
        selectedEmployerData.value = null
      }
      emit('update:modelValue', value)
    }
  })

  // Search employers function with minimum 2 characters requirement
  const searchEmployers = async searchQuery => {
    if (!searchQuery?.length || searchQuery.length < 2) {
      employerSuggestions.value = []
      return
    }

    try {
      isLoading.value = true
      const { data } = await find('employers', {
        filters: {
          $or: [{ name: { $containsi: searchQuery } }, { acronym: { $containsi: searchQuery } }]
        },
        fields: ['id', 'name', 'acronym'],
        pagination: {
          limit: 15
        },
        sort: { name: 'asc' }
      })
      employerSuggestions.value = data || []
    } catch (error) {
      console.error('Error searching employers:', error)
      employerSuggestions.value = []
    } finally {
      isLoading.value = false
    }
  }

  watchThrottled(
    query,
    newQuery => {
      searchEmployers(newQuery)
    },
    { throttle: 500 }
  )

  // Handle blur event to reset query
  function onBlur() {
    setTimeout(() => {
      query.value = ''
      employerSuggestions.value = []
    }, 100)
  }

  // Options employeurs
  const employerOptions = computed(() => {
    return employerSuggestions.value.map(employer => ({
      value: employer.id,
      label: employer.name,
      employer: employer
    }))
  })

  // Simple display function
  const displayEmployer = employerId => {
    if (!employerId) return ''

    // First check if we have the selected employer data stored
    if (selectedEmployerData.value && selectedEmployerData.value.id === employerId) {
      return selectedEmployerData.value.name
    }

    // Check in current suggestions
    const employer = employerSuggestions.value.find(emp => emp.id === employerId)
    if (employer) {
      selectedEmployerData.value = employer
      return employer.name
    }

    // Fallback to ID if name not found
    return `Employeur ID: ${employerId}`
  }

  onMounted(async () => {
    if (props.modelValue && !selectedEmployerData.value) {
      try {
        const { data } = await find('employers', {
          filters: { id: props.modelValue },
          fields: ['id', 'name', 'acronym']
        })
        if (data && data.length > 0) {
          selectedEmployerData.value = data[0]
        }
      } catch (error) {
        console.error('Error loading employer:', error)
      }
    }
  })

  // Watch for changes in modelValue to load employer data
  watch(
    () => props.modelValue,
    async (newValue, oldValue) => {
      // Only fetch if value changed and we don't already have the data
      if (newValue && newValue !== oldValue && (!selectedEmployerData.value || selectedEmployerData.value.id !== newValue)) {
        try {
          const { data } = await find('employers', {
            filters: { id: newValue },
            fields: ['id', 'name', 'acronym']
          })
          if (data && data.length > 0) {
            selectedEmployerData.value = data[0]
          }
        } catch (error) {
          console.error('Error loading employer:', error)
        }
      } else if (!newValue) {
        selectedEmployerData.value = null
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div class="relative">
    <Combobox class="w-full" v-model="selectedEmployer" as="div">
      <div class="relative">
        <ComboboxInput
          class="form-input w-full"
          :class="{ 'border-red-500': error }"
          :placeholder="placeholder"
          :displayValue="displayEmployer"
          @change="query = $event.target.value"
          @blur="onBlur"
        />

        <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <path d="m6 9 4 4 4-4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </ComboboxButton>
      </div>

      <div class="relative w-full">
        <ComboboxOptions
          class="absolute z-10 mt-0.5 max-h-64 w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
          v-if="query.length >= 2"
        >
          <ComboboxOption class="cursor-default select-none px-3 py-2 text-gray-500" v-if="isLoading" disabled>
            Recherche en cours...
          </ComboboxOption>

          <!-- Message quand aucun employeur trouvé -->
          <ComboboxOption
            class="cursor-default select-none bg-green-50 px-3 py-2 text-sm text-green-600"
            v-if="!isLoading && query.length >= 2 && employerSuggestions.length === 0"
            disabled
          >
            <div class="flex items-center space-x-2">
              <svg class="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>Soyez tranquille, votre employeur n'est pas sur Profil Public</span>
            </div>
          </ComboboxOption>

          <ComboboxOption
            class="cursor-pointer px-2 py-1 text-left"
            v-for="option in employerOptions"
            v-slot="{ active, selected }"
            :key="option.value"
            :value="option.value"
            :class="[active ? 'bg-zinc-100' : '', selected ? 'font-semibold' : '']"
          >
            {{ option.label }}
          </ComboboxOption>
        </ComboboxOptions>
      </div>
    </Combobox>

    <p class="mt-1 text-sm text-red-600" v-if="error">
      {{ error }}
    </p>
  </div>
</template>
